import React, { ReactNode, useCallback } from 'react';
import { FetchResult, useMutation } from '@apollo/client';
import {
  ASSIGN_TASK,
  GET_LOGGED_IN_PERSON,
  GET_TASKS,
  RESOLVE_TASK,
  GET_MANUAL_TASKS_RESOLUTIONS,
} from './queries';
import { AssigneeType, JobType, Person, Task, TaskResolution, TaskStatus, TaskType } from './types';
import { toastError, toastSuccess } from 'bench-internal-theme';
import { useLocation } from 'react-router-dom';
import { useV2Query } from '../utils';
import useV2Mutation from 'utils/useV2Mutation';

type AssignTaskVariables = {
  taskId: Task['id'];
  assignedPersonId: number | null;
};

type AssignTaskResponse = {
  assignTask: Task;
};

type ResolveTaskVariables = {
  taskId: Task['id'];
  resolution: string;
  note?: string;
};

type ResolveTaskResponse = {
  resolveTask: Task;
};

type ManualTaskResolutions = {
  jobType: JobType;
  taskType: TaskType;
  assigneeType: AssigneeType;
  manualResolutions: TaskResolution[];
};

export type TaskManagerContext = {
  loggedInPerson: Person;
  assignTask: (task: Task) => Promise<FetchResult<unknown>> | undefined;
  resolveTask: (
    task: Task,
    resolution: TaskStatus.PAUSED | TaskResolution,
    note?: string,
  ) => Promise<FetchResult<unknown>>;
  assignTaskLoading: boolean;
  resolveTaskLoading: boolean;
  getManualTaskResolutions: (
    jobType: JobType,
    taskType: TaskType,
    assigneeType: AssigneeType,
  ) => TaskResolution[];
  sessionAssignedTasks?: Task[];
  clearSessionAssignedTasks?: () => void;
};

const TaskManagerContext = React.createContext<TaskManagerContext | Record<string, undefined>>({});

export const TaskManagerContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const location = useLocation();

  const { data, loading } = useV2Query(GET_LOGGED_IN_PERSON);
  const [sessionAssignedTasks, setSessionAssignedTasks] = React.useState<Task[]>([]);
  const { data: manualTasksResolutionsData, loading: manualTaskResolutionsLoading } = useV2Query(
    GET_MANUAL_TASKS_RESOLUTIONS,
  );

  const [assignTaskMutation, { loading: assignTaskLoading }] = useMutation<
    AssignTaskResponse,
    AssignTaskVariables
  >(ASSIGN_TASK, {
    onCompleted: ({ assignTask }) =>
      toastSuccess(
        (assignTask.assignedPerson ? 'Task assigned to ' : 'Task un-assigned from ') +
          data?.loggedInPerson.fullName,
        '',
        { style: { zIndex: 100 } },
      ),
    onError: () => toastError('There was an issue assigning your task'),
  });
  const [resolveTaskMutation, { loading: resolveTaskLoading }] = useV2Mutation<
    ResolveTaskResponse,
    ResolveTaskVariables
  >(RESOLVE_TASK, {
    onError: () => toastError('There was an issue resolving your task'),
    refetchQueries: [GET_TASKS],
  });

  const isUnassignedView = new URLSearchParams(location.search).get('assignedPersonId') === 'null';

  const updateSessionAssignedTasks = useCallback(
    (task: Task) => {
      if (task.assignedPerson?.id === data?.loggedInPerson.id) {
        let newSessionAssignedTasks = sessionAssignedTasks;
        if (isUnassignedView) {
          const taskIndex = sessionAssignedTasks.findIndex(t => t.id === task.id);
          newSessionAssignedTasks[taskIndex] = { ...task, assignedPerson: null };
        } else {
          newSessionAssignedTasks = sessionAssignedTasks.filter(t => t.id !== task.id);
        }
        setSessionAssignedTasks(newSessionAssignedTasks);
      } else {
        setSessionAssignedTasks([
          ...sessionAssignedTasks,
          { ...task, assignedPerson: data?.loggedInPerson },
        ]);
      }
    },
    [data, sessionAssignedTasks, isUnassignedView],
  );
  const clearSessionAssignedTasks = useCallback(() => setSessionAssignedTasks([]), []);

  const assignTask = useCallback(
    (task: Task) => {
      if (task.status !== TaskStatus.PENDING_WORK) return;
      updateSessionAssignedTasks(task);
      const variables = {
        assignedPersonId: task.assignedPerson ? null : parseInt(data?.loggedInPerson?.id),
        taskId: task.id,
      };
      return assignTaskMutation({ variables });
    },
    [data, assignTaskMutation, updateSessionAssignedTasks],
  );

  const resolveTask = useCallback(
    (task, resolution, note?) => {
      if (resolveTaskLoading) return Promise.resolve({});
      return resolveTaskMutation({
        variables: {
          taskId: task.id,
          resolution,
          note,
        },
      });
    },
    [resolveTaskMutation, resolveTaskLoading],
  );

  const getManualTaskResolutions = (
    jobType: JobType,
    taskType: TaskType,
    assigneeType: AssigneeType,
  ) => {
    const taskResolutions: ManualTaskResolutions[] =
      manualTasksResolutionsData.manualTasksResolutions;
    return (
      taskResolutions?.filter(
        manualTask =>
          manualTask.jobType === jobType &&
          manualTask.taskType === taskType &&
          manualTask.assigneeType === assigneeType,
      )[0]?.manualResolutions ?? []
    );
  };

  return (
    <TaskManagerContext.Provider
      value={{
        loggedInPerson: data?.loggedInPerson,
        assignTask,
        assignTaskLoading,
        resolveTask,
        resolveTaskLoading,
        getManualTaskResolutions,
        sessionAssignedTasks,
        clearSessionAssignedTasks,
      }}
    >
      {!loading && !manualTaskResolutionsLoading && children}
    </TaskManagerContext.Provider>
  );
};

export function useTaskManagerContext(): TaskManagerContext {
  return React.useContext(TaskManagerContext) as TaskManagerContext;
}
